import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { filter, find } from 'lodash-es'
import { Stack, Typography } from '@mui/material'
import ElementListPane, {
  COMMENTS_TAB_ID,
  IMAGE_CARDS_TAB_ID,
  TABLES_TAB_ID,
  TwoPaneElementDrawer,
} from '@editor/components/ElementListPane'
import OrthographicControls from '@editor/components/OrthographicControls'
import PlanElementDrawer from '@editor/components/PlanElementDrawer'
import { LoadingIndicator } from '@ui/feedback'
import { useControlStore, useEditElementStore, useModelStore } from '@editorStores'
import {
  useTypeInteraction,
  useHighlightFromSelection,
  useBlockScene,
  useEnterEditorPage,
  useSelectedPlanElementId,
} from '@editorHooks'
import { WidgetPortal, ColoringControls } from '@editorComponents'
import { useQueriesLoading } from 'src/state/hooks'
import {
  modelDrawOptions,
  structureDrawOptions,
} from '../../components/Tools/EditElementSelect/constants'
import {
  DrawModePane,
  PlanImageCardForm,
  PlanTableForm,
  StructuralPlanningDrawer,
} from './components'
import { DRAW_MODE_ELEMENT_LABELS } from './components/EndDrawModeButton/constants'
import {
  useStructuralPlanningSelection,
  useStructuralPlanningQueries,
  useStructuralPlanningEsc,
} from './hooks'
import useStructuralPlanningElementList from './hooks/useStructuralPlanningElementList'

const TAB_ID_TO_PLAN_ELEMENT_TYPE: Record<string, 'comment' | 'table' | 'image-card'> = {
  [COMMENTS_TAB_ID]: 'comment',
  [TABLES_TAB_ID]: 'table',
  [IMAGE_CARDS_TAB_ID]: 'image-card',
}

const StructuralPlanning = (): ReactElement => {
  useEnterEditorPage('structural-planning')

  const {
    params: { mode, selectedStiffeningElements, storey },
    actions: { setStorey, multiSelectElements, reset },
    modes: { isStiffeningSelectionMode: isSelectionMode, isPlanTableMode, isPlanImageCardMode },
  } = useStructuralPlanningSelection()

  useStructuralPlanningEsc()
  // STATE

  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)

  const setColoringMode = useControlStore(state => state.setColoringMode)

  // QUERIES

  const queryResults = useStructuralPlanningQueries()
  const isLoading = useQueriesLoading(queryResults)

  // ACTIONS

  useTypeInteraction('none', isSelectionMode)

  useHighlightFromSelection(!selectedStiffeningElements?.length)

  useBlockScene(isLoading)

  const removeRip = useModelStore(state => state.removeRip)

  // remove all unsaved elements if mode changes
  // TODO: implement it the same way for columns, beams etc.

  const removeUnsavedElementsFromStore = useCallback(
    (mode: StructuralPlanningModes) => {
      if (mode !== 'wall-rip') {
        const rips = useModelStore.getState().model.rips
        const localRips = filter(rips, { is_local: true })
        localRips.map(rip => removeRip(rip.position_guid))
      }
    },
    [removeRip],
  )

  useEffect(() => {
    removeUnsavedElementsFromStore(mode as StructuralPlanningModes)

    return () => {
      removeUnsavedElementsFromStore(mode as StructuralPlanningModes)
    }
  }, [mode, removeUnsavedElementsFromStore])

  const modeLabel = useMemo(() => {
    const allOptions = [...modelDrawOptions, ...structureDrawOptions]
    const option = find(allOptions, el => el.value === mode)
    return option?.label
  }, [mode])

  const drawModeElementLabel = mode ? DRAW_MODE_ELEMENT_LABELS[mode] : undefined

  useEffect(() => {
    setColoringMode('problemView')
  }, [setColoringMode])

  const selectedPlanTableId = useSelectedPlanElementId('table')
  const selectedPlanImageCardId = useSelectedPlanElementId('image-card')

  const drawerContent = useMemo(() => {
    if (isLoading) return <LoadingIndicator data-cy="loading-structural-planning" />

    if (isPlanTableMode && selectedPlanTableId) return <PlanTableForm />

    if (isPlanImageCardMode && selectedPlanImageCardId) return <PlanImageCardForm />

    return (
      <>
        {modeLabel && (
          <Typography variant="h5" data-cy="mode-label" pt={1} pb={0} px={1} align="center">
            {modeLabel}
          </Typography>
        )}
        {drawModeElementLabel ? (
          <DrawModePane
            elementLabel={drawModeElementLabel}
            onEnd={reset}
            data-cy={`btn-exit-${mode}-mode`}
          >
            <StructuralPlanningDrawer />
          </DrawModePane>
        ) : (
          <StructuralPlanningDrawer />
        )}
      </>
    )
  }, [
    isLoading,
    isPlanImageCardMode,
    isPlanTableMode,
    modeLabel,
    drawModeElementLabel,
    mode,
    reset,
    selectedPlanImageCardId,
    selectedPlanTableId,
  ])

  const elementListBinding = useStructuralPlanningElementList()
  const [activePrimary, setActivePrimary] = useState<string>('')

  const elementListCollapsed = useControlStore(state => state.elementListCollapsed)

  const activePlanElementType = elementListCollapsed
    ? null
    : TAB_ID_TO_PLAN_ELEMENT_TYPE[activePrimary] ?? null

  return (
    <>
      <WidgetPortal>
        <Stack direction="row" spacing={1}>
          <OrthographicControls
            isOrthographic={!!storey}
            setIsOrthographic={ortho => {
              if (ortho) {
                setStorey('0')
              } else setStorey(null)
            }}
          />
          <ColoringControls
            onSelectElements={elements => {
              if (typeof storey === 'string') {
                // In floorplan mode
                setMultiActiveElements(elements)
              } else {
                // in 3d mode
                multiSelectElements(elements)
              }
            }}
          />
        </Stack>
      </WidgetPortal>
      <TwoPaneElementDrawer
        collapsed={elementListCollapsed}
        list={<ElementListPane {...elementListBinding} onActivePrimaryChange={setActivePrimary} />}
      >
        <PlanElementDrawer planElementType={activePlanElementType} fallback={drawerContent} />
      </TwoPaneElementDrawer>
    </>
  )
}

export default StructuralPlanning
